import { Controller } from "@hotwired/stimulus";
import TomSelect from "tom-select";

// Connects to data-controller="tom-select-filter"
export default class extends Controller {
  static targets = ["select"];
  static values = {
    options: Object,
    placeholder: String,
    noResultsText: { type: String, default: "Geen resultaten gevonden" },
    submitDelay: { type: Number, default: 120 },
  };
  connect() {
    // Guard against double initialisation (Turbo morph/restore can re-run connect)
    if (this.selectTarget.tomselect) return;

    const defaultOptions = {
      plugins: ["remove_button"],
      maxItems: null,
      valueField: "value",
      labelField: "text",
      searchField: "text",
      create: false,
      // Keep the dropdown open so multiple options can be picked in one go.
      closeAfterSelect: false,
      placeholder: this.placeholderValue || "Search and select...",
      render: {
        dropdown: function () {
          return '<div class="ts-dropdown-container"></div>';
        },
        no_results: (data, escape) => {
          return `<div class="no-results">${this.noResultsTextValue}</div>`;
        },
      },
      dropdownParent: "body",
      onDropdownOpen: (dropdown) => {
        this.flipDropdownIfNeeded(dropdown);
      },
      // While the dropdown is open the user may still be picking options.
      // Defer filtering until the dropdown closes so we submit only once
      // instead of reloading (and closing the dropdown) after every click.
      onChange: () => {
        this.onSelectionChanged();
      },
      onDropdownClose: () => {
        if (this.pendingSubmit) {
          this.pendingSubmit = false;
          this.scheduleSubmit();
        }
      },
    };

    const options = { ...defaultOptions, ...this.optionsValue };
    this.tomSelect = new TomSelect(this.selectTarget, options);
    this.ready = true;

    // A form that copies this filter in on submit (filter-params) already
    // carries the selection, so the delayed reload is no longer needed. Worse,
    // it would replace that form while its request is in flight, and Turbo
    // then drops the turbo-stream response.
    this.form = this.element.closest("form");
    this.cancelSubmit = this.cancelSubmit.bind(this);
    this.form?.addEventListener("filter-params:synced", this.cancelSubmit);
  }

  cancelSubmit() {
    this.pendingSubmit = false;
    clearTimeout(this.submitTimer);
  }

  // The dropdown is appended to <body> and positioned absolutely, so its
  // `top` is in document coordinates while getBoundingClientRect() reports
  // viewport coordinates. Flipping the dropdown above the field has to add
  // the scroll offset back in, or on a scrolled page it lands a screenful too
  // high and looks like the field simply refuses to open.
  flipDropdownIfNeeded(dropdown) {
    const control = this.element.querySelector(".ts-control");
    if (!control) return;

    const controlRect = control.getBoundingClientRect();
    const spaceBelow = window.innerHeight - controlRect.bottom;
    const spaceAbove = controlRect.top;
    if (spaceBelow >= dropdown.offsetHeight || spaceAbove < dropdown.offsetHeight) return;

    dropdown.style.top = `${controlRect.top + window.scrollY - dropdown.offsetHeight - 5}px`;
  }

  onSelectionChanged() {
    // Ignore the change events tom-select fires while building the initial value.
    if (!this.ready) return;

    if (this.tomSelect && this.tomSelect.isOpen) {
      // Dropdown still open: wait for it to close before filtering.
      this.pendingSubmit = true;
    } else {
      // e.g. removing a chip via its × button: filter right away.
      this.scheduleSubmit();
    }
  }

  scheduleSubmit() {
    const form = this.element.closest("form");
    if (!form) return;

    clearTimeout(this.submitTimer);
    this.submitTimer = setTimeout(() => {
      form.requestSubmit();
    }, this.submitDelayValue);
  }

  disconnect() {
    clearTimeout(this.submitTimer);
    this.form?.removeEventListener("filter-params:synced", this.cancelSubmit);
    if (this.tomSelect) {
      this.tomSelect.destroy();
      this.tomSelect = null;
    }
  }
}
